import React from "react";
import RelationSetCard from "./RelationSetCard";
import { Button, CardHeader, Card, CardBody, Chip } from "@heroui/react";
import { Split, Merge } from "lucide-react";
import {
  RelationItem,
  RelationshipBuilderRootRelation,
  RelationSetType,
} from "./relationship.d";
import { Trans } from "@lingui/react/macro";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/utils";

const RelationshipBuilder = ({
  relationData,
  onChange,
  items,
}: {
  relationData: RelationshipBuilderRootRelation;
  onChange: (relations: RelationshipBuilderRootRelation) => void;
  items: RelationItem[];
}) => {
  const [expandedSetId, setExpandedSetId] = React.useState<string | null>(null);

  const handleDeleteResourceSet = (setId: string) => {
    onChange({
      ...relationData,
      relationSets: relationData.relationSets.filter((set) => set.id !== setId),
    });
  };

  const handleUpdateResourceSet = (updatedSet: RelationSetType) => {
    onChange({
      ...relationData,
      relationSets: relationData.relationSets.map((set) =>
        set.id === updatedSet.id ? updatedSet : set,
      ),
    });
  };

  const toggleExpand = (setId: string) => {
    setExpandedSetId(expandedSetId === setId ? null : setId);
  };

  const handleAddResourceSet = () => {
    const newSet: RelationSetType = {
      id: `rs-${Date.now()}`,
      logicType: "AND",
      items: [],
    };

    onChange({
      ...relationData,
      relationSets: [...relationData.relationSets, newSet],
    });

    // Automatically expand the newly added set
    setExpandedSetId(newSet.id);
  };

  const toggleSetsLogicType = () => {
    const newLogicType: "AND" | "OR" =
      relationData.logicType === "AND" ? "OR" : "AND";
    onChange({
      ...relationData,
      logicType: newLogicType,
    });
  };

  return (
    <div>
      <AnimatePresence>
        {relationData.relationSets.length > 1 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, height: 0 }}
            animate={{ opacity: 1, scale: 1, height: "auto" }}
            exit={{ opacity: 0, scale: 0.95, height: 0 }}
            transition={{ duration: 0.15 }}
            className="w-full"
          >
            <Card shadow="sm" className="w-full">
              <CardHeader className="flex items-center justify-between">
                <p className="text-md font-medium">
                  <Trans>Povezave med skupinami sredstev</Trans>
                </p>
                <Chip
                  color={
                    relationData.logicType === "AND" ? "primary" : "primary"
                  }
                  className="cursor-pointer transition-opacity hover:opacity-80"
                  onClick={toggleSetsLogicType}
                >
                  <div className="flex select-none items-center gap-2">
                    {relationData.logicType === "AND" ? (
                      <>
                        <Merge size={16} />
                        <span>
                          <Trans>IN</Trans>
                        </span>
                      </>
                    ) : (
                      <>
                        <Split size={16} />
                        <span>
                          <Trans>ALI</Trans>
                        </span>
                      </>
                    )}
                  </div>
                </Chip>
              </CardHeader>
              <CardBody>
                <div className="flex items-center gap-2">
                  {relationData.logicType === "OR" ? (
                    <Split size={16} />
                  ) : (
                    <Merge size={16} />
                  )}
                  <p className="text-sm text-default-600">
                    {relationData.logicType === "AND" ? (
                      <Trans>
                        Vse skupine sredstev so potrebne za to storitev
                      </Trans>
                    ) : (
                      <Trans>
                        Katera koli skupina sredstev je dovolj za to storitev
                      </Trans>
                    )}
                  </p>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={cn("mt-4 flex flex-col items-center gap-4", {
          "mt-0": relationData.relationSets.length <= 1,
        })}
      >
        <AnimatePresence>
          {relationData.relationSets.map((set, i) => (
            <motion.div
              key={set.id}
              initial={{ opacity: 0, scale: 0.95, height: 0 }}
              animate={{ opacity: 1, scale: 1, height: "auto" }}
              exit={{ opacity: 0, scale: 0.95, height: 0 }}
              transition={{ duration: 0.15 }}
              className="flex w-full flex-col items-center gap-4"
            >
              <RelationSetCard
                relationSet={set}
                onChange={handleUpdateResourceSet}
                onDelete={() => handleDeleteResourceSet(set.id)}
                isExpanded={expandedSetId === set.id}
                onToggleExpand={() => toggleExpand(set.id)}
                items={items}
              />

              {i < relationData.relationSets.length - 1 && (
                <Chip
                  color={
                    relationData.logicType === "AND" ? "primary" : "primary"
                  }
                  variant="solid"
                  className="flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-80"
                  onClick={toggleSetsLogicType}
                >
                  <div className="flex select-none items-center gap-2">
                    {relationData.logicType === "AND" ? (
                      <>
                        <Merge size={16} />
                        <span>
                          <Trans>IN</Trans>
                        </span>
                      </>
                    ) : (
                      <>
                        <Split size={16} />
                        <span>
                          <Trans>ALI</Trans>
                        </span>
                      </>
                    )}
                  </div>
                </Chip>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        <Button
          onPress={handleAddResourceSet}
          startContent={<Merge size={16} />}
          className="w-full"
          variant="flat"
        >
          <Trans>Dodaj skupino sredstev</Trans>
        </Button>
      </div>
    </div>
  );
};

export { RelationshipBuilder };
